"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  FileSearch,
  FolderKanban,
  ImageIcon,
  Sparkles,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

// Process step illustrations
const ILLUSTRATIONS = {
  analyze: "/images/landing/how-it-works/analyze-illustration.svg",
  organize: "/images/landing/how-it-works/organize-illustration.svg",
  reimagine: "/images/landing/how-it-works/reimagine-illustration.svg",
};

interface StepCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  bgColor: string;
  darkColor: string;
  lightColor: string;
  illustrationPath: string;
  isInView: boolean;
  delay: number;
  index: number;
  features: string[];
  isMobile: boolean;
}

function StepCard({
  icon,
  title,
  description,
  color,
  bgColor,
  darkColor,
  lightColor,
  illustrationPath,
  isInView,
  delay,
  index,
  features,
  isMobile,
}: StepCardProps) {
  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
  };

  const featureVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        delay: delay + 0.1 + i * 0.1,
      },
    }),
  };

  if (isMobile) {
    return (
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={cardVariants}
        className="relative bg-white rounded-xl shadow-md overflow-hidden border border-gray-50"
      >
        {/* Number Badge */}
        <div className="absolute top-3 right-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-smokeyBlack text-xs font-semibold text-white shadow-md">
          {index}
        </div>

        {/* Illustration */}
        <div className={`relative h-44 ${bgColor}`}>
          <Image
            src={illustrationPath}
            alt={title}
            className="object-cover p-0"
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
          />
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
            <h3 className="text-xl font-semibold text-smokeyBlack">{title}</h3>
          </div>

          <p className="text-darkGray text-sm mb-4">{description}</p>

          {/* Key Features - Simplified for mobile */}
          <div className="space-y-2">
            {features.slice(0, 2).map((feature, i) => (
              <motion.div
                key={`${title}-mobile-feature-${feature.substring(0, 10).replace(/\s+/g, "-")}`}
                custom={i}
                variants={featureVariants}
                className="flex items-center gap-2"
              >
                <div className={`h-1.5 w-1.5 rounded-full ${darkColor}`} />
                <p className="text-xs text-darkGray">{feature}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  }

  // Desktop version
  return (
    <motion.div
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={cardVariants}
      className="flex flex-col"
    >
      {/* Main Content Box */}
      <div className="grid grid-cols-2 gap-6 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-50 p-1">
        {/* Left Content Side */}
        <div className="p-6 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-3 rounded-xl ${color} shadow-md`}>{icon}</div>
            <div className="flex items-center gap-2">
              <div
                className={`h-8 w-8 rounded-full ${darkColor} flex items-center justify-center text-white font-semibold`}
              >
                {index}
              </div>
              <h3 className="text-2xl font-semibold text-smokeyBlack">
                {title}
              </h3>
            </div>
          </div>

          <p className="text-darkGray mb-6">{description}</p>

          {/* Key Features */}
          <div className="space-y-3">
            {features.map((feature, i) => (
              <motion.div
                key={`${title}-desktop-feature-${feature.substring(0, 10).replace(/\s+/g, "-")}`}
                custom={i}
                variants={featureVariants}
                className={`flex items-center gap-3 p-3 rounded-lg ${lightColor}`}
              >
                <div className={`h-2 w-2 rounded-full ${darkColor}`} />
                <p className="text-sm text-darkGray">{feature}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Illustration Side */}
        <div className={`relative ${bgColor} rounded-xl overflow-hidden`}>
          <Image
            src={illustrationPath}
            alt={title}
            className="object-cover p-0"
            fill
            sizes="(min-width: 768px) 50vw, 100vw"
          />
        </div>
      </div>
    </motion.div>
  );
}

export function HowMasterkeyAIWorks() {
  const [isInView, setIsInView] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll animation for desktop
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const opacity = useTransform(
    scrollYProgress,
    [0, 0.2, 0.8, 1],
    [0.4, 1, 1, 0.4],
  );
  const scale = useTransform(
    scrollYProgress,
    [0, 0.2, 0.8, 1],
    [0.95, 1, 1, 0.95],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Set initial state
    checkMobile();

    // Add resize listener
    window.addEventListener("resize", checkMobile);

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const steps = [
    {
      icon: <FileSearch className="h-6 w-6 text-white" />,
      title: "Analyze",
      description:
        "Instantly understand a property's true value and potential with our AI analysis.",
      color: "bg-blue-500",
      bgColor: "bg-blue-50",
      darkColor: "bg-blue-600",
      lightColor: "bg-blue-50",
      illustrationPath: "/images/landing/how-it-works/analyze-illustration.png",
      delay: 0.1,
      index: 1,
      features: [
        "Get AI-powered property insights and valuations",
        "Compare to similar homes for better price negotiations",
        "Identify renovation needs and costs upfront",
      ],
    },
    {
      icon: <FolderKanban className="h-6 w-6 text-white" />,
      title: "Organize",
      description:
        "Keep your entire home buying journey organized in one centralized platform.",
      color: "bg-emerald-500",
      bgColor: "bg-emerald-50",
      darkColor: "bg-emerald-600",
      lightColor: "bg-emerald-50",
      illustrationPath:
        "/images/landing/how-it-works/organize-illustration.png",
      delay: 0.3,
      index: 2,
      features: [
        "Save properties with personalized notes",
        "Store all documents and get AI summaries",
        "Manage tasks and collaborate with co-buyers",
      ],
    },
    {
      icon: <ImageIcon className="h-6 w-6 text-white" />,
      title: "Reimagine",
      description:
        "Visualize renovation possibilities before you buy with AI-generated designs.",
      color: "bg-amber-500",
      bgColor: "bg-amber-50",
      darkColor: "bg-amber-600",
      lightColor: "bg-amber-50",
      illustrationPath:
        "/images/landing/how-it-works/reimagine-illustration.png",
      delay: 0.5,
      index: 3,
      features: [
        "Transform interiors with AI visualization",
        "Test different styles before renovation",
        "Calculate potential value added by improvements",
      ],
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-gradient-to-b from-chalk to-white py-16 sm:py-28"
      aria-labelledby="how-it-works-title"
      id="how-it-works"
    >
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 top-32 h-64 w-64 rounded-full bg-blue-500/5 blur-3xl" />
        <div className="absolute right-0 top-1/3 h-96 w-96 rounded-full bg-emerald-500/5 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-amber-500/5 blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mx-auto mb-16 max-w-2xl text-center"
        >
          <div className="inline-flex items-center justify-center gap-2 mb-6 rounded-full bg-white/80 px-3 py-1.5 shadow-sm backdrop-blur-sm">
            <Sparkles className="h-4 w-4 text-canary" />
            <span className="text-sm font-semibold text-smokeyBlack">
              Powered by Artificial Intelligence
            </span>
          </div>

          <h2
            id="how-it-works-title"
            className="mb-4 text-4xl sm:text-5xl font-bold text-smokeyBlack"
          >
            How Masterkey <span className="text-canary">Works</span>
          </h2>
          <p className="text-lg text-darkGray">
            Three powerful tools to transform your home buying experience
          </p>
        </motion.header>

        {/* Main Content */}
        <div ref={containerRef} className="mx-auto">
          {/* Desktop Experience - Horizontal Cards */}
          {!isMobile && (
            <motion.div
              style={{ opacity, scale }}
              className="hidden md:block max-w-6xl mx-auto"
            >
              <div className="space-y-20">
                {steps.map((step) => (
                  <StepCard
                    key={step.title}
                    icon={step.icon}
                    title={step.title}
                    description={step.description}
                    color={step.color}
                    bgColor={step.bgColor}
                    darkColor={step.darkColor}
                    lightColor={step.lightColor}
                    illustrationPath={step.illustrationPath}
                    isInView={isInView}
                    delay={step.delay}
                    index={step.index}
                    features={step.features}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* Mobile Experience - Card Grid */}
          {isMobile && (
            <div className="grid grid-cols-1 gap-6 md:hidden">
              {steps.map((step) => (
                <StepCard
                  key={step.title}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                  color={step.color}
                  bgColor={step.bgColor}
                  darkColor={step.darkColor}
                  lightColor={step.lightColor}
                  illustrationPath={step.illustrationPath}
                  isInView={isInView}
                  delay={step.delay}
                  index={step.index}
                  features={step.features}
                  isMobile={isMobile}
                />
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <Link
            href="/login"
            className="inline-flex items-center rounded-full bg-canary px-8 py-4 font-bold text-smokeyBlack transition-all hover:bg-canary/90 group shadow-md hover:shadow-lg"
            aria-label="Try Masterkey Today"
          >
            <span>Try Masterkey Today</span>
            <ArrowRight
              className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1"
              aria-hidden="true"
            />
          </Link>
          <p className="mt-4 text-sm text-darkGray">No credit card required.</p>
        </motion.div>
      </div>
    </section>
  );
}
